import { pickT } from "@repo/shared";
import type { LocalizedString } from "@repo/shared/types";
import { PageHeader } from "@/components/page-header";
import { DocButton } from "@/components/ui/doc-button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTabParam } from "@/hooks/use-tab-param";
import { useFlow } from "@/lib/hooks/use-flows";
import { usePortfolioCollections } from "@/lib/hooks/use-portfolio-collections";
import { useActiveRiskModels } from "@/lib/hooks/use-risk-models";
import { useLocalize } from "@/lib/i18n/use-localize";
import { T } from "./flow-editor.i18n";
import { FlowFieldsTab } from "./flow-fields-tab";
import { FlowPagesTab } from "./flow-pages";
import { FlowSettingsTab } from "./flow-settings-tab";

type FlowEditorProps = {
  flowId: string;
};

const TAB_VALUES = ["settings", "fields", "pages"] as const;

export function FlowEditor({ flowId }: FlowEditorProps) {
  const localize = useLocalize();
  // QL-195 — the URL is the source of truth for the active tab so refreshes and
  // deep-links land on the right one.
  const [activeTab, handleTabChange] = useTabParam(TAB_VALUES, "settings");
  const { flow, isLoading, mutate } = useFlow(flowId);
  const { models: riskModelsRaw } = useActiveRiskModels();
  const { collections: portfolioCollectionsRaw } = usePortfolioCollections();

  // Use flow language for all flow editing UI
  const flowLanguage = flow?.language || "en_GB";
  const t = pickT(T, flowLanguage);

  const riskModels = riskModelsRaw.map((r: any) => ({
    id: r.id,
    name: localize(r.nameI18n as LocalizedString),
  }));

  // QL-338 — only active collections are offered (the API refuses attaching
  // an inactive one). The flow's current collection stays listed even if it
  // has since gone inactive, so saving unrelated settings never silently
  // drops it.
  const portfolioCollections = portfolioCollectionsRaw
    .filter(
      (c: any) => c.riskModelId !== null && (c.isActive || c.id === flow?.portfolioCollectionId),
    )
    .map((c: any) => ({
      id: c.id,
      name: localize(c.nameI18n as LocalizedString),
      riskModelId: c.riskModelId!,
      // Collections with an FS-model relation force the flow's FS model.
      financialSituationModelId: (c.financialSituationModelId as string | null) ?? null,
      currency: c.currency as string,
    }));

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="space-y-6">
        <p className="text-muted-foreground">{t.flowNotFound}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[
          { label: t.settings, href: "/settings" },
          { label: t.flows, href: "/settings/flows" },
          { label: flow.name },
        ]}
        title={flow.name}
        description={flow.description || undefined}
        actions={
          <DocButton docSlug="creating-flows" variant="outline" size="sm" className="gap-2">
            {t.documentation}
          </DocButton>
        }
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList>
          <TabsTrigger value="settings">{t.settingsTab}</TabsTrigger>
          <TabsTrigger value="fields">
            {t.fieldsTab}
            {flow.fields && flow.fields.length > 0 && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {flow.fields.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pages">
            {t.pagesTab}
            {flow.pages && flow.pages.length > 0 && (
              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                {flow.pages.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Kept mounted (hidden when inactive) so unsaved edits survive a visit
            to Fields or Pages; Radix unmounts inactive content by default and the
            form holds its edits in local state. */}
        <TabsContent value="settings" forceMount className="mt-6 data-[state=inactive]:hidden">
          <FlowSettingsTab
            flow={flow}
            riskModels={riskModels}
            portfolioCollections={portfolioCollections}
          />
        </TabsContent>

        <TabsContent value="fields" className="mt-6">
          <FlowFieldsTab flowId={flow.id} flowLanguage={flowLanguage} mutate={mutate} />
        </TabsContent>

        <TabsContent value="pages" className="mt-6">
          <FlowPagesTab
            flowId={flow.id}
            flowLanguage={flowLanguage}
            pages={flow.pages || []}
            availableFields={flow.fields || []}
            portfolioCollectionId={flow.portfolioCollectionId ?? null}
            multiGoal={flow.multiGoal ?? false}
            multiPortfolio={flow.multiPortfolio ?? false}
            requirementIssues={flow.requirementIssues}
            mutate={mutate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
